import type { VariationPlacement } from "@popperjs/core";
import clsx from "clsx";
import React, { ReactNode, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

// This is a wrapper around the Popper library so we can easily replace it with
// different versions and implementations
export const Popper = ({
  children,
  className,
  allowOverflow,
  flip,
  open,
  anchorEl,
  placement,
  clickable = true,
}: {
  children: ReactNode;
  allowOverflow?: boolean;
  flip?: boolean;
  open: boolean;
  placement?: VariationPlacement;
  anchorEl: Element | null;
  className?: string;
  clickable?: boolean;
}) => {
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);

  const flipModifier =
    !flip && allowOverflow
      ? [
          {
            name: "flip",
            enabled: false,
          },
        ]
      : [];

  const preventOverflowModifier = allowOverflow
    ? [
        {
          name: "preventOverflow",
          enabled: false,
        },
      ]
    : [];

  const { styles, attributes } = usePopper(anchorEl, popperElement, {
    placement,
    modifiers: [
      {
        name: "computeStyles",
        options: {
          gpuAcceleration: false, // true by default
        },
      },
      ...flipModifier,
      ...preventOverflowModifier,
    ],
  });

  if (!open) return null;

  // In some cases, interacting with something inside a popper will cause a rerender that detaches the anchorEl
  // This happened in hovers on in-line reacts, and the button to create a new react ended up on the top-left corner of the page
  if (anchorEl && !anchorEl.isConnected) {
    return null;
  }

  // We use createPortal here to avoid having to deal with overflow problems and styling from the current child
  // context, by placing the Popper element directly into the document root
  // Rest of usage from https://popper.js.org/react-popper/v2/
  return (
    <React.Fragment>
      {createPortal(
        <div
          ref={setPopperElement}
          className={clsx(
            {
              "pointer-events-none": !clickable,
              /*[classes.tooltip]: tooltip,
          [classes.default]: !tooltip,
          [classes.hideOnTouchScreens]: hideOnTouchScreens,*/
            },
            className,
          )}
          style={styles.popper}
          {...attributes.popper}
        >
          {children}
        </div>,
        document.body,
      )}
    </React.Fragment>
  );
};
